import {Location, Photo, RealEstateEstimateState as EstimateState} from '@/model/api';
import {NetworkError} from '@/model/errors/NetworkError';
import {Estimate} from '@/model/Estimate';
import {HttpClient} from '@/model/http';
import {isLeft, map} from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';

interface TemplateForNewEstimate {
    id: string;
    created: false;
    offerType: string;
    estateType: string;
}

interface ExistingEstimate {
    id: string;
    created: true;
}

export default class RealEstateEstimate implements Estimate<EstimateState> {
    private static readonly BASE_ULR = '/api/real-estate/estimates';
    private static readonly HEADERS = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    };

    constructor(
        private estimate: TemplateForNewEstimate | ExistingEstimate,
        private readonly http: HttpClient,
    ) {
    }

    public async uploadPhoto(file: File, position: number): Promise<Photo> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('position', String(position));

        return this.http.request(this.estimateUrl(this.getExistingEstimateId()) + '/photos', {
            method: 'POST',
            body: formData,
        }).then(response => {
            if (!response.ok) {
                throw new Error('Photo could not be uploaded');
            }

            return response.json();
        }).catch(this.handleNetworkError);
    }

    public async changePhotoDescription(photoId: string, description: string): Promise<void> {
        const estimateId = this.getExistingEstimateId();

        return this.http.request(this.estimateUrl(estimateId) + '/photos/' + photoId + '/description', {
            method: 'PUT',
            headers: RealEstateEstimate.HEADERS,
            body: JSON.stringify({description}),
        }).then(response => {
            if (!response.ok) {
                throw new Error(
                    'Photo description change failed for photo '
                    + photoId + ' in estate description ' + estimateId,
                );
            }
        }).catch(this.handleNetworkError);
    }

    public async changePhotoPosition(photoId: string, position: number): Promise<void> {
        const response = await this.http.safeRequest(
            this.estimateUrl(this.getExistingEstimateId()) + '/photos/' + photoId + '/position',
            {
                method: 'PUT',
                headers: RealEstateEstimate.HEADERS,
                body: JSON.stringify({position}),
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    public async removePhoto(photoId: string): Promise<void> {
        const estimateId = this.getExistingEstimateId();

        return this.http.request(this.estimateUrl(estimateId) + '/photos/' + photoId, {
            method: 'DELETE',
            headers: RealEstateEstimate.HEADERS,
        }).then(response => {
            if (!response.ok) {
                throw new Error(
                    'Photo removal failed for photo' + photoId + 'in estate description ' + estimateId,
                );
            }
        }).catch(this.handleNetworkError);
    }

    public async rotatePhoto(photoId: string, angle: number): Promise<void> {
        const estimateId = this.getExistingEstimateId();

        return this.http.request(this.estimateUrl(estimateId) + '/photos/' + photoId + '/rotate', {
            method: 'POST',
            headers: RealEstateEstimate.HEADERS,
            body: JSON.stringify({angle}),
        }).then(response => {
            if (!response.ok) {
                throw new Error(
                    'Photo rotation failed for photo' + photoId + 'in estate description ' + estimateId,
                );
            }
        }).catch(this.handleNetworkError);
    }

    public async updateParameters(description: object): Promise<EstimateState> {
        const estimateId = this.getExistingEstimateId();

        return this.http.request(this.estimateUrl(estimateId) + '/parameters', {
            method: 'PATCH',
            headers: RealEstateEstimate.HEADERS,
            body: JSON.stringify(description),
        }).then((response) => {
            if (!response.ok) {
                throw new Error('Parameters update failed for estate description ' + estimateId);
            }

            return response.json();
        }).catch(this.handleNetworkError);
    }

    public selectLocation(location: Location): Promise<EstimateState> {
        const estimate = this.estimate;

        if (estimate.created) {
            return this.http.request(this.estimateUrl(estimate.id) + '/location', {
                method: 'PUT',
                headers: RealEstateEstimate.HEADERS,
                body: JSON.stringify({id: location.id}),
            }).then((response) => {
                if (!response.ok) {
                    throw new Error('Location selection failed for estimate ' + estimate);
                }

                return response.json();
            }).catch(this.handleNetworkError);
        }

        return this.http.request(this.estimateUrl(estimate.id), {
            method: 'PUT',
            headers: RealEstateEstimate.HEADERS,
            body: JSON.stringify({
                estateType: estimate.estateType,
                offerType: estimate.offerType,
                location: location.id,
            }),
        }).then((response) => {
            if (!response.ok) {
                throw new Error('Location selection failed for estimate ' + estimate);
            }

            this.estimate = {
                id: estimate.id,
                created: true,
            };

            return response.json();
        }).catch(this.handleNetworkError);
    }

    public async createEstimate() {
        const payloadOrError = await this.http.safeRequest<{calculationUrl: string}>(
            this.estimateUrl(this.getExistingEstimateId()) + '/calculate',
            { method: 'POST', headers: RealEstateEstimate.HEADERS },
        );

        return pipe(payloadOrError, map(json => json.calculationUrl));
    }

    public async recalculateWithNewEstates(): Promise<void> {
        return this.http.request(this.estimateUrl(this.getExistingEstimateId()) + '/recalculate', {
            method: 'POST',
            headers: RealEstateEstimate.HEADERS,
        }).then(response => {
            if (!response.ok) {
                throw new Error('Could not clear estimate estates');
            }
        }).catch(this.handleNetworkError);
    }

    public async getEstimate(): Promise<EstimateState> {
        return this.http.request(this.estimateUrl(this.getExistingEstimateId()))
            .then(response => {
                if (!response.ok) {
                    throw new Error(`[${response.status}] Could not get estimate`);
                }

                return response.json();
            }).catch(this.handleNetworkError);
    }

    private estimateUrl(estimateId: string): string {
        return `${RealEstateEstimate.BASE_ULR}/${estimateId}`;
    }

    private handleNetworkError(error: TypeError): void {
        throw new NetworkError(error.message);
    }

    private getExistingEstimateId(): string {
        const estimate = this.estimate;

        if (estimate.created) {
            return estimate.id;
        }

        throw new Error('Estimate ID not set, estimate wasn\'t created yet');
    }
}
